import React, { JSX, useRef, useEffect, use, useMemo, useState } from 'react';
import {
  Text,
  RichText,
  Field,
  LinkField,
  Link,
  ImageField,
  Image,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { ItemValue } from 'components/types/ListItem';
import ThemeContext from 'components/context/ThemeContext';
import { classNames } from 'assets/utils/helper';
import { getMarginSize } from 'assets/utils/getMarginSize';
import { StyleguideComponentProps } from 'lib/component-props';
import { observeInView } from 'src/hooks/observeInView';

type SpotlightNarrowProps = StyleguideComponentProps & {
  fields: {
    MarginSize: {
      fields: {
        heading: {
          value: string;
        };
      };
    };
    BaseHeadline: Field<string>;
    BaseDescription: Field<string>;
    BaseAlignment: ItemValue;
    BaseOverlayColor: ItemValue;
    BaseLink: LinkField;
    BaseLinkType: ItemValue;
    DesktopImage: ImageField;
    MobileImage?: ImageField;
    Parallax: {
      value: boolean;
    };
    PhotoVariant?: ItemValue;
    DarkText: {
      value: boolean;
    };
    BasePhoneNumber: Field<string>;
  };
};

const SpotlightNarrow = (props: SpotlightNarrowProps): JSX.Element => {
  if (!props || !props.fields) {
    return <div></div>;
  }

  const marginSize = getMarginSize(
    props?.fields?.MarginSize?.fields?.heading?.value
  );
  const themeContext = use(ThemeContext);
  const theme = themeContext?.themeState?.theme;
  const elementRef = useRef<HTMLDivElement | null>(null);
  const animRef = useRef<HTMLAnchorElement | null>(null);
  const [isLgUp, setIsLgUp] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const mq = window.matchMedia('(min-width: 1024px)');
    const onChange = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsLgUp('matches' in e ? e.matches : (e as MediaQueryList).matches);
    onChange(mq);
    mq.addEventListener?.(
      'change',
      onChange as (e: MediaQueryListEvent) => void
    );
    return () =>
      mq.removeEventListener?.(
        'change',
        onChange as (e: MediaQueryListEvent) => void
      );
  }, []);
  let prevScroll = 0;

  const handleScroll = () => {
    if (
      elementRef.current &&
      (theme === 'sfcon' || theme === 'sacon') &&
      elementRef.current.classList.contains('parallax')
    ) {
      const rect = elementRef.current?.getBoundingClientRect();
      if (rect.bottom > 0 && rect.top < window.innerHeight) {
        if (rect.top < prevScroll) {
          const currImg = elementRef.current.querySelector('img');
          if (currImg) {
            const imgTransform =
              (-(currImg.height - rect.height) / 2) *
              ((window.innerHeight - (rect.top - rect.height / 2)) /
                window.innerHeight);
            currImg.style.transform = `translateY(${imgTransform}px)`;
          }
        } else {
          const currImg = elementRef.current.querySelector('img');
          if (currImg) {
            const imgTransform =
              (-(currImg.height - rect.height) / 2) *
              ((window.innerHeight - (rect.top - rect.height / 2)) /
                window.innerHeight);
            currImg.style.transform = `translateY(${imgTransform}px)`;
          }
        }
      }
      prevScroll = rect.top;
    }
  };

  useEffect(() => {
    if (!animRef.current) return;

    observeInView(animRef.current);
  }, []);

  useEffect(() => {
    if (elementRef.current) {
      const rect = elementRef.current?.getBoundingClientRect();
      prevScroll = rect.top;
    }
  }, [elementRef]);

  useEffect(() => {
    if (
      typeof window !== 'undefined' &&
      (theme === 'sfcon' || theme === 'sacon') &&
      elementRef?.current?.classList.contains('parallax')
    ) {
      window.addEventListener('scroll', handleScroll);
    }
    return () => window.removeEventListener('resize', handleScroll);
  }, [theme]);

  const chosenImage: ImageField | undefined = useMemo(() => {
    const desktop = props.fields.DesktopImage?.value?.src
      ? props.fields.DesktopImage
      : undefined;
    const mobile = props.fields.MobileImage?.value?.src
      ? props.fields.MobileImage
      : undefined;
    if (desktop && mobile) return isLgUp ? desktop : mobile;
    return desktop ?? mobile;
  }, [props.fields.DesktopImage, props.fields.MobileImage, isLgUp]);
  const hasImage = Boolean(chosenImage?.value?.src);
  const safeAlt =
    chosenImage?.value?.alt ?? props.fields?.BaseHeadline?.value ?? '';
  const phoneNumber = props.fields?.BasePhoneNumber?.value?.trim();
  const linkText = props.fields?.BaseLink?.value?.text?.trim();

  const linkClassName = classNames(
    props.fields?.BaseLinkType?.name
      ? `btn-mobile-fw btn-${props.fields.BaseLinkType.name.toLowerCase()}`
      : 'btn-mobile-fw',
    'spotlight-narrows-anim animated'
  );

  return (
    <div
      className={classNames('overhauled', marginSize)}
      key={`spotlight-narrow-${props.rendering.uid}`}
      id={`spotlight-narrow-${props.rendering.uid}`}
    >
      <div
        className={classNames(
          'spotlight narrow inner-vertical-spacing',
          hasImage ? 'image-bg' : 'bg-secondary',
          (theme === 'sfcon' || theme === 'sacon') &&
            props.fields?.Parallax?.value === true
            ? 'parallax'
            : '',
          props?.fields?.PhotoVariant?.name
            ? props?.fields?.PhotoVariant.name.toLowerCase()
            : ''
        )}
        ref={elementRef}
      >
        {hasImage && (
          <Image
            field={chosenImage as ImageField}
            alt={safeAlt}
            className="bg-img"
          />
        )}
        {hasImage && (
          <div
            className={`overlay ${
              props.fields?.BaseAlignment?.name
                ? `${props.fields?.BaseAlignment?.name?.toLowerCase()}`
                : ''
            } ${
              props.fields?.BaseOverlayColor?.name &&
              props?.fields.BaseOverlayColor?.name !== 'none'
                ? `${props.fields?.BaseOverlayColor?.name?.toLowerCase()}`
                : ''
            } ${hasImage && props?.fields.BaseOverlayColor?.name !== 'none' ? 'spotlight-gradient' : ''}`}
          ></div>
        )}
        <div
          className={classNames(
            'jss-inner-container content-wrapper',
            props.fields?.BaseAlignment?.name
              ? `align-${props.fields?.BaseAlignment?.name?.toLowerCase()}`
              : '',
            props?.fields?.DarkText?.value === true ? 'dark-text' : ''
          )}
        >
          <>
            {props.fields?.BaseHeadline?.value &&
              (hasImage ? (
                <Text
                  tag="h2"
                  className="osf-display-h2 headline"
                  field={props.fields.BaseHeadline}
                />
              ) : (
                <Text
                  tag="h2"
                  className="headline"
                  field={props.fields.BaseHeadline}
                />
              ))}
            {props.fields?.BaseDescription?.value && (
              <RichText
                tag={
                  !(props.fields.BaseHeadline.value.length > 0) &&
                  !(
                    props.fields.BaseLink?.value?.href &&
                    props.fields.BaseLink?.value?.href?.length > 0
                  )
                    ? 'p'
                    : ''
                }
                className={
                  !(props.fields.BaseHeadline.value.length > 0) &&
                  !(
                    props.fields.BaseLink?.value?.href &&
                    props.fields.BaseLink?.value?.href?.length > 0
                  )
                    ? 'osf-display-h2'
                    : 'osf-intro-copy description'
                }
                field={props.fields.BaseDescription}
              />
            )}
          </>
          {phoneNumber ? (
            <a
              href={`tel:${phoneNumber}`}
              className={linkClassName}
              ref={animRef}
            >
              {linkText || phoneNumber}
            </a>
          ) : (
            props.fields?.BaseLink?.value?.href &&
            props.fields?.BaseLinkType?.name && (
              <Link
                field={props.fields.BaseLink}
                className={linkClassName}
                ref={animRef}
              />
            )
          )}
        </div>
      </div>
    </div>
  );
};

export default SpotlightNarrow;
